"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Search, XIcon, Filter } from "lucide-react";
import FormInput from "@/components/ui/FormInput/FormInput";
import BottomSheet from "@/components/portal/BottomSheet";
import Button from "@/components/ui/Button";
import FilterPanel from "@/components/pages/search/FilterPanel";
import FilterContent, { sortOptions } from "@/components/pages/search/FilterContent";
import SearchResults from "@/components/pages/search/SearchResults";
import SearchSuggestions, { hrefFor } from "@/components/pages/search/SearchSuggestions";
import { useSearchFilters } from "./useSearchFilters";
import { useTagOptions } from "./useTagOptions";
import { useSearchCatalog } from "./useSearchCatalog";
import { useSearchByQuery } from "./useSearchByQuery";
import { useSearchSuggestions } from "@/queries/search";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
export default function SearchPage() {
  const $ = _c(82);
  const router = useRouter();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchBoxRef = useRef(null);
  const filters = useSearchFilters();
  const {
    genreOptions,
    countryOptions
  } = useTagOptions();
  let t0;
  if ($[0] !== filters.search) {
    t0 = filters.search.trim();
    $[0] = filters.search;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isSearchingByQuery = t0.length > 0;
  const debouncedSearch = useDebouncedValue(filters.search, 350);
  const debouncedRange = useDebouncedValue(filters.range, 350);
  const t1 = !isSearchingByQuery;
  let t2;
  if ($[2] !== debouncedRange || $[3] !== filters.yearType || $[4] !== t1) {
    t2 = {
      range: debouncedRange,
      yearType: filters.yearType,
      enabled: t1
    };
    $[2] = debouncedRange;
    $[3] = filters.yearType;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const catalog = useSearchCatalog(t2);
  const queryResults = useSearchByQuery(debouncedSearch);
  const results = isSearchingByQuery ? queryResults : catalog;
  const {
    data: suggestionsData,
    isFetching: isSuggestFetching,
    isError: isSuggestError
  } = useSearchSuggestions(debouncedSearch, isSuggestOpen);
  let t3;
  if ($[6] !== suggestionsData?.data?.suggestions) {
    t3 = suggestionsData?.data?.suggestions ?? [];
    $[6] = suggestionsData?.data?.suggestions;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const suggestions = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setActiveIndex(-1);
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== debouncedSearch) {
    t5 = [debouncedSearch];
    $[9] = debouncedSearch;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const handleClickOutside = function handleClickOutside(e) {
        if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
          setIsSuggestOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    };
    t7 = [];
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setIsSuggestOpen(false);
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const closeSuggest = t8;
  let t9;
  if ($[14] !== activeIndex || $[15] !== isSuggestOpen || $[16] !== router || $[17] !== suggestions) {
    t9 = e_0 => {
      if (!isSuggestOpen || suggestions.length === 0) {
        return;
      }
      if (e_0.key === "ArrowDown") {
        e_0.preventDefault();
        setActiveIndex(prev => (prev + 1) % suggestions.length);
      } else {
        if (e_0.key === "ArrowUp") {
          e_0.preventDefault();
          setActiveIndex(prev_0 => prev_0 <= 0 ? suggestions.length - 1 : prev_0 - 1);
        } else {
          if (e_0.key === "Escape") {
            setIsSuggestOpen(false);
          } else {
            if (e_0.key === "Enter" && activeIndex >= 0) {
              const target = suggestions[activeIndex];
              const href = target ? hrefFor(target) : null;
              if (href) {
                e_0.preventDefault();
                setIsSuggestOpen(false);
                router.push(href);
              }
            }
          }
        }
      }
    };
    $[14] = activeIndex;
    $[15] = isSuggestOpen;
    $[16] = router;
    $[17] = suggestions;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleKeyDown = t9;
  let t10;
  if ($[19] !== countryOptions || $[20] !== filters.handleCountriesChange || $[21] !== filters.handleGenresChange || $[22] !== filters.handleRangeChange || $[23] !== filters.handleRangeChangeFinal || $[24] !== filters.handleYearType || $[25] !== filters.range || $[26] !== filters.selectedCountries || $[27] !== filters.selectedGenres || $[28] !== filters.updateQuery || $[29] !== filters.yearType || $[30] !== genreOptions) {
    t10 = {
      genres: genreOptions,
      countries: countryOptions,
      sortOptions,
      selectedGenres: filters.selectedGenres,
      selectedCountries: filters.selectedCountries,
      yearType: filters.yearType,
      range: filters.range,
      handleGenresChange: filters.handleGenresChange,
      handleCountriesChange: filters.handleCountriesChange,
      handleYearType: filters.handleYearType,
      handleRangeChange: filters.handleRangeChange,
      handleRangeChangeFinal: filters.handleRangeChangeFinal,
      updateQuery: filters.updateQuery
    };
    $[19] = countryOptions;
    $[20] = filters.handleCountriesChange;
    $[21] = filters.handleGenresChange;
    $[22] = filters.handleRangeChange;
    $[23] = filters.handleRangeChangeFinal;
    $[24] = filters.handleYearType;
    $[25] = filters.range;
    $[26] = filters.selectedCountries;
    $[27] = filters.selectedGenres;
    $[28] = filters.updateQuery;
    $[29] = filters.yearType;
    $[30] = genreOptions;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const filterPanelProps = t10;
  let t11;
  if ($[32] !== filterPanelProps) {
    t11 = <FilterPanel {...filterPanelProps} />;
    $[32] = filterPanelProps;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  let t13;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Search className="w-5 h-5" />;
    t13 = <XIcon className="w-5 h-5" />;
    $[34] = t12;
    $[35] = t13;
  } else {
    t12 = $[34];
    t13 = $[35];
  }
  let t14;
  let t15;
  if ($[36] !== filters) {
    t14 = e_1 => {
      filters.handleSearchChange(e_1.target.value);
      setIsSuggestOpen(true);
    };
    t15 = () => {
      filters.handleSearchChange("");
      setIsSuggestOpen(false);
    };
    $[36] = filters;
    $[37] = t14;
    $[38] = t15;
  } else {
    t14 = $[37];
    t15 = $[38];
  }
  let t16;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setIsSuggestOpen(true);
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== filters.search || $[41] !== handleKeyDown || $[42] !== t14 || $[43] !== t15) {
    t17 = <FormInput name="search" suffixIcon={t12} clearIcon={t13} value={filters.search} placeholder={"\u0641\u06CC\u0644\u0645\u060C \u0633\u0631\u06CC\u0627\u0644\u060C \u0628\u0627\u0632\u06CC\u06AF\u0631 \u0648 \u0698\u0627\u0646\u0631"} onChange={t14} onClear={t15} onFocus={t16} onKeyDown={handleKeyDown} autoComplete="off" />;
    $[40] = filters.search;
    $[41] = handleKeyDown;
    $[42] = t14;
    $[43] = t15;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== activeIndex || $[46] !== isSearchingByQuery || $[47] !== isSuggestError || $[48] !== isSuggestFetching || $[49] !== isSuggestOpen || $[50] !== suggestions) {
    t18 = isSuggestOpen && isSearchingByQuery && <SearchSuggestions suggestions={suggestions} loading={isSuggestFetching} activeIndex={activeIndex} onNavigate={closeSuggest} isError={isSuggestError} />;
    $[45] = activeIndex;
    $[46] = isSearchingByQuery;
    $[47] = isSuggestError;
    $[48] = isSuggestFetching;
    $[49] = isSuggestOpen;
    $[50] = suggestions;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== t17 || $[53] !== t18) {
    t19 = <div ref={searchBoxRef} className="relative">{t17}{t18}</div>;
    $[52] = t17;
    $[53] = t18;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="block md:hidden"><Button variant="secondary" size="lg" fullWidth={true} icon={Filter} onClick={() => setIsFilterModalOpen(true)}>فیلتر</Button></div>;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  const t21 = isSearchingByQuery ? queryResults.persons : undefined;
  let t22;
  if ($[56] !== results) {
    t22 = () => results.refetch();
    $[56] = results;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== results.handleLoadMore || $[59] !== results.hasNextPage || $[60] !== results.isError || $[61] !== results.isFetchingNextPage || $[62] !== results.isLoading || $[63] !== results.items || $[64] !== t21 || $[65] !== t22) {
    t23 = <section className="search-results-grid w-full mt-5 pb-8"><SearchResults items={results.items} loading={results.isLoading} hasMore={results.hasNextPage} isLoadingMore={results.isFetchingNextPage} onLoadMore={results.handleLoadMore} persons={t21} isError={results.isError} onRetry={t22} /></section>;
    $[58] = results.handleLoadMore;
    $[59] = results.hasNextPage;
    $[60] = results.isError;
    $[61] = results.isFetchingNextPage;
    $[62] = results.isLoading;
    $[63] = results.items;
    $[64] = t21;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== t19 || $[68] !== t23) {
    t24 = <div className="content flex-1 flex flex-col gap-6">{t19}{t20}{t23}</div>;
    $[67] = t19;
    $[68] = t23;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  let t25;
  if ($[70] !== t11 || $[71] !== t24) {
    t25 = <div className="flex h-full w-full gap-6">{t11}{t24}</div>;
    $[70] = t11;
    $[71] = t24;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => setIsFilterModalOpen(false);
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  let t27;
  if ($[74] !== filterPanelProps) {
    t27 = <FilterContent {...filterPanelProps} />;
    $[74] = filterPanelProps;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  let t28;
  if ($[76] !== isFilterModalOpen || $[77] !== t27) {
    t28 = <BottomSheet open={isFilterModalOpen} onClose={t26}>{t27}</BottomSheet>;
    $[76] = isFilterModalOpen;
    $[77] = t27;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  let t29;
  if ($[79] !== t25 || $[80] !== t28) {
    t29 = <div className="mx-auto container max-w-3xl px-4 md:px-12 pt-25">{t25}{t28}</div>;
    $[79] = t25;
    $[80] = t28;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  return t29;
}